import { Component, Object3D } from '@wonderlandengine/api';
import { property } from '@wonderlandengine/api/decorators.js';
import { quat, vec3 } from 'gl-matrix';
import type { AvatarTrackingObjects } from './avatar-tracking.js';
import { ArmIK } from './arm-ik-solver.js';

/**
 * Minimal three-point adapter for the imported Mayo humanoid.
 * Network poses live outside the skeleton: moving the body must never move the
 * received head/hand poses. Arm IK rotates the upper arms and forearms to reach
 * the tracked wrists without changing segment lengths.
 */
export class RiggedAvatarFollow extends Component {
    static TypeName = 'rigged-avatar-follow';

    // Mayo already faces correctly without an extra half-turn after import.
    // Do not infer the imported model's forward direction from its VRM version.
    @property.float(0)
    facingYawDegrees = 0;

    // Shared controller-local yaw magnitude: left negative, right positive.
    @property.float(90)
    handYawDegrees = 90;

    // Current visually calibrated hand-local X magnitude: both positive.
    @property.float(90)
    handPitchDegrees = 90;

    // Signed hand-local Y correction: right +value, left -value, after pitch.
    @property.float(-40)
    handRollDegrees = -40;

    @property.float(5)
    minElbowBendDegrees = 5;

    @property.float(150)
    maxElbowBendDegrees = 150;

    // Let forearm roll follow the hand instead of forcing all roll into the wrist.
    @property.float(1)
    forearmTwistWeight = 1;

    private leftArm: ArmIK | null = null;
    private rightArm: ArmIK | null = null;
    private bones: AvatarTrackingObjects | null = null;
    private targets: AvatarTrackingObjects | null = null;
    private readonly headOffset = vec3.create();
    private readonly authoredRotation = quat.create();
    private readonly facingCorrection = quat.create();
    private readonly leftHandCorrection = quat.create();
    private readonly rightHandCorrection = quat.create();
    private readonly restRotations = new Map<Object3D, quat>();
    private readonly restHandTransforms = new Map<Object3D, Float32Array>();
    private readonly position = vec3.create();
    private readonly offset = vec3.create();
    private readonly forward = vec3.create();
    private readonly rotation = quat.create();
    private readonly bodyRotation = quat.create();
    private yaw = 0;

    initialize(bones: AvatarTrackingObjects, targets: AvatarTrackingObjects): void {
        this.bones = bones;
        this.targets = targets;
        this.leftArm = ArmIK.create(this.object, bones.leftHand, 'Left');
        this.rightArm = ArmIK.create(this.object, bones.rightHand, 'Right');
        this.object.getRotationWorld(this.authoredRotation);
        bones.head.getPositionWorld(this.headOffset);
        this.object.getPositionWorld(this.position);
        vec3.sub(this.headOffset, this.headOffset, this.position);
        quat.invert(this.rotation, this.authoredRotation);
        // Keep authored scale in this offset, including any scale on the mount.
        vec3.transformQuat(this.headOffset, this.headOffset, this.rotation);

        for (const bone of [bones.head, bones.leftHand, bones.rightHand]) {
            if (!bone) continue;
            const rest = bone.getRotationWorld(quat.create());
            quat.multiply(rest, this.rotation, rest);
            this.restRotations.set(bone, rest);
            if (bone !== bones.head) {
                this.restHandTransforms.set(bone, bone.getTransformLocal(new Float32Array(8)));
            }
        }
    }

    /** Includes signed body-local error: +X means displaced toward avatar right. */
    getTrackingDiagnostics() {
        const head = this.targets?.head;
        if (!head) return null;
        const headWorld = head.getPositionWorld(vec3.create());
        const inverseBody = quat.invert(quat.create(), this.bodyRotation);
        const snapshot = (target: Object3D | null, hand: Object3D | null, arm: ArmIK | null) => {
            if (!target || !hand) return null;
            const targetWorld = target.getPositionWorld(vec3.create());
            const wristWorld = hand.getPositionWorld(vec3.create());
            const targetFromHead = vec3.sub(vec3.create(), targetWorld, headWorld);
            const error = vec3.sub(vec3.create(), wristWorld, targetWorld);
            vec3.transformQuat(targetFromHead, targetFromHead, inverseBody);
            vec3.transformQuat(error, error, inverseBody);
            return {
                receiving: Boolean(target.getComponent('networked')?.active),
                hasArmIK: Boolean(arm),
                targetFromHeadBodyAxes: Array.from(targetFromHead),
                wristMinusTargetBodyAxes: Array.from(error),
                arm: arm?.getTrackingDiagnostics(targetWorld, this.bodyRotation) ?? null,
            };
        };
        return {
            headWorld: Array.from(headWorld),
            bodyWorld: Array.from(this.object.getPositionWorld(vec3.create())),
            minElbowBendDegrees: this.minElbowBendDegrees,
            maxElbowBendDegrees: this.maxElbowBendDegrees,
            forearmTwistWeight: this.forearmTwistWeight,
            handRollDegrees: this.handRollDegrees,
            left: snapshot(this.targets!.leftHand, this.bones!.leftHand, this.leftArm),
            right: snapshot(this.targets!.rightHand, this.bones!.rightHand, this.rightArm),
        };
    }

    update(): void {
        const bones = this.bones;
        const targets = this.targets;
        if (!bones || !targets || !targets.head.getComponent('networked')?.active) return;

        quat.setAxisAngle(this.facingCorrection, [0, 1, 0], this.facingYawDegrees * Math.PI / 180);
        const handYaw = Math.abs(this.handYawDegrees) * Math.PI / 180;
        quat.setAxisAngle(this.leftHandCorrection, [0, 1, 0], -handYaw);
        quat.setAxisAngle(this.rightHandCorrection, [0, 1, 0], handYaw);
        targets.head.getForwardWorld(this.forward);
        if (Math.hypot(this.forward[0], this.forward[2]) > 1e-5) {
            this.yaw = Math.atan2(-this.forward[0], -this.forward[2]);
        }
        quat.setAxisAngle(this.bodyRotation, [0, 1, 0], this.yaw);
        quat.multiply(this.bodyRotation, this.bodyRotation, this.facingCorrection);
        quat.multiply(this.bodyRotation, this.bodyRotation, this.authoredRotation);
        this.object.setRotationWorld(this.bodyRotation);
        vec3.transformQuat(this.offset, this.headOffset, this.bodyRotation);
        targets.head.getPositionWorld(this.position);
        vec3.sub(this.position, this.position, this.offset);
        this.object.setPositionWorld(this.position);

        this.copyJointPose(bones.head, targets.head);
        const handPitch = Math.abs(this.handPitchDegrees) * Math.PI / 180;
        this.copyHandPose(bones.leftHand, targets.leftHand, this.leftHandCorrection, handPitch, this.leftArm);
        this.copyHandPose(bones.rightHand, targets.rightHand, this.rightHandCorrection, handPitch, this.rightArm);
    }

    private copyJointPose(bone: Object3D, target: Object3D, handCorrection?: quat, handPitch = 0, copyPosition = true, arm: ArmIK | null = null): void {
        if (copyPosition) target.getPositionWorld(this.position);
        target.getRotationWorld(this.rotation);
        // Post-multiply: correct controller-local Y, not the world's Y axis.
        if (handCorrection) quat.multiply(this.rotation, this.rotation, handCorrection);
        quat.multiply(this.rotation, this.rotation, this.facingCorrection);
        quat.multiply(this.rotation, this.rotation, this.authoredRotation);
        quat.multiply(this.rotation, this.rotation, this.restRotations.get(bone)!);
        // Append to the aligned joint pose so X is the hand's own local axis.
        if (handCorrection) {
            quat.rotateX(this.rotation, this.rotation, handPitch);
            const rollSign = bone === this.bones?.leftHand ? -1 : 1;
            quat.rotateY(this.rotation, this.rotation, rollSign * this.handRollDegrees * Math.PI / 180);
        }
        arm?.alignWristRoll(this.rotation, this.forearmTwistWeight);
        bone.setRotationWorld(this.rotation);
        if (copyPosition) bone.setPositionWorld(this.position);
    }

    private copyHandPose(bone: Object3D | null, target: Object3D | null, correction: quat, pitch: number, arm: ArmIK | null): void {
        if (!bone || !target) return;
        if (target.getComponent('networked')?.active) {
            if (arm) {
                target.getPositionWorld(this.position);
                arm.solve(this.position, this.bodyRotation, this.minElbowBendDegrees, this.maxElbowBendDegrees);
                // Orientation only: forcing wrist position here would undo length constraints.
                this.copyJointPose(bone, target, correction, pitch, false, arm);
            } else {
                // Incomplete/unrecognized arm hierarchy keeps three-point compatibility.
                this.copyJointPose(bone, target, correction, pitch);
            }
        } else {
            // On XR exit keep the hands attached in their authored rest pose.
            if (arm) arm.restore();
            else bone.setTransformLocal(this.restHandTransforms.get(bone)!);
        }
    }
}